import { apiFetch } from '../lib/apiFetch';

// ============================================
// TYPES
// ============================================

export interface RegionNode {
  id: string;
  name: string;
  code: string;
  /** URL slug used by `/r/:slug` links (BON-203 round 8). The backend derives
   *  it the same way the admin/regional DTOs do; optional for back-compat with
   *  any caller/response that predates it. */
  slug?: string;
  /** Display color set on a root region; null/absent falls back to a palette-by-index. */
  color?: string | null;
  directorUserId?: string | null;
  /** Assigned regional director's full name, or null/absent if unassigned. */
  directorName?: string | null;
  parentId: string | null;
  venueCount: number;
  children: RegionNode[];
}

export type AssignmentMode = 'auto' | 'pin' | 'exclude';

export interface MapVenue {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  latitude: number | null;
  longitude: number | null;
  regionId: string | null;
  regionName: string | null;
  exception: AssignmentMode;
}

export type GeoType = 'state' | 'county' | 'city' | 'neighborhood';

export interface RegionComponent {
  id: string;
  geoType: GeoType;
  geoRef: string;
  label: string;
}

export interface ComponentConflict {
  geoType: string;
  geoRef: string;
  label: string;
  ownerRegionId: string;
  ownerRegionName: string;
}

export interface RegionDetail {
  id: string;
  name: string;
  code: string;
  color: string | null;
  parentId: string | null;
  parentName: string | null;
  directorUserId: string | null;
  directorName: string | null;
  inheritedDirectorName: string | null;
  inheritedFromRegionName: string | null;
  subregionCount: number;
  componentCount: number;
  venueCount: number;
}

export interface PreviewVenue {
  id: string;
  name: string;
  address: string;
  fromRegionId: string | null;
  fromRegionName: string | null;
  toRegionId: string;
  toRegionName: string;
}

export interface ReassignSummary {
  assigned: number;
  unchanged: number;
  skipped: number;
}

/** Error carrying the affected venues when a composition change conflicts. */
export class ComponentOverlapError extends Error {
  conflicts: ComponentConflict[];
  constructor(message: string, conflicts: ComponentConflict[]) {
    super(message);
    this.name = 'ComponentOverlapError';
    this.conflicts = conflicts;
  }
}

// Backend errors are { status: 'error', message: string, ... }.
export async function errorMessage(res: Response, fallback: string): Promise<string> {
  const data = await res.json().catch(() => ({}) as Record<string, unknown>);
  return typeof data.message === 'string' ? data.message : fallback;
}

// ============================================
// REGION CRUD
// ============================================

export async function createRegion(name: string, parentId?: string | null): Promise<RegionNode> {
  const res = await apiFetch('/api/v1/regions', {
    method: 'POST',
    body: JSON.stringify({ name, parentId: parentId || null }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to create region: ${res.status}`));
  return res.json();
}

export async function renameRegion(id: string, name: string): Promise<void> {
  const res = await apiFetch(`/api/v1/regions/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to rename region: ${res.status}`));
}

export async function deleteRegion(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/regions/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to delete region: ${res.status}`));
}

export async function moveRegion(id: string, parentId: string | null): Promise<void> {
  const res = await apiFetch(`/api/v1/regions/${id}/parent`, {
    method: 'PUT',
    body: JSON.stringify({ parentId }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to move region: ${res.status}`));
}

/** Set (or clear, with null) a root region's display color. */
export async function setRegionColor(id: string, color: string | null): Promise<void> {
  const res = await apiFetch(`/api/v1/regions/${id}/color`, {
    method: 'PUT',
    body: JSON.stringify({ color }),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `Failed to set region color: ${res.status}`));
}

export async function mergeRegion(id: string, intoRegionId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/regions/${id}/merge`, {
    method: 'POST',
    body: JSON.stringify({ intoRegionId }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to merge region: ${res.status}`));
}

// ============================================
// FETCH
// ============================================

export async function fetchRegions(): Promise<RegionNode[]> {
  const res = await apiFetch('/api/v1/regions');
  if (!res.ok) throw new Error(`Failed to fetch regions: ${res.status}`);
  const data = await res.json();
  return data.regions;
}

export async function fetchRegionDetail(id: string): Promise<RegionDetail> {
  const res = await apiFetch(`/api/v1/regions/${id}/detail`);
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to fetch region: ${res.status}`));
  return (await res.json()).region;
}

export async function fetchMapVenues(): Promise<MapVenue[]> {
  const res = await apiFetch('/api/v1/regions/venues/map');
  if (!res.ok) throw new Error(`Failed to fetch map venues: ${res.status}`);
  return (await res.json()).venues;
}

/** Active venues that are members of a region — assigned to it or any subregion. */
export async function fetchRegionVenues(id: string): Promise<MapVenue[]> {
  const res = await apiFetch(`/api/v1/regions/${id}/venues`);
  if (!res.ok) throw new Error(`Failed to fetch region venues: ${res.status}`);
  return (await res.json()).venues;
}

/** One entry in a region's activity feed (audit event). */
export interface RegionActivityEntry {
  id: string;
  eventType: string;
  summary: string;
  /** Acting user's full name, or "System" for system-initiated changes. */
  actorName: string;
  createdAt: string;
}

/** Recent activity for a region (newest first) — powers the detail panel. */
export async function fetchRegionActivity(id: string): Promise<RegionActivityEntry[]> {
  const res = await apiFetch(`/api/v1/regions/${id}/activity`);
  if (!res.ok) throw new Error(`Failed to fetch region activity: ${res.status}`);
  return (await res.json()).activity;
}

export interface TriageVenue {
  id: string;
  name: string;
  city: string;
  state: string;
  needsCoordinates: boolean;
  suggestedRegionId: string | null;
  suggestedRegionName: string | null;
}

/** Active venues with no region, each with a best-effort suggested region. */
export async function fetchUnassignedVenues(): Promise<TriageVenue[]> {
  const res = await apiFetch('/api/v1/regions/venues/unassigned');
  if (!res.ok) throw new Error(`Failed to fetch unassigned venues: ${res.status}`);
  return (await res.json()).venues;
}

/** GeoJSON boundary (union of the region's component polygons), or null. */
export async function fetchRegionBoundary(id: string): Promise<GeoJSON.Geometry | null> {
  const res = await apiFetch(`/api/v1/regions/${id}/boundary`);
  if (!res.ok) throw new Error(`Failed to fetch region boundary: ${res.status}`);
  return (await res.json()).geojson;
}

export interface RegionBoundary {
  regionId: string;
  geojson: GeoJSON.Geometry | null;
}

/** Every region's boundary polygon, for highlighting regions on the landing map. */
export async function fetchRegionBoundaries(): Promise<RegionBoundary[]> {
  const res = await apiFetch('/api/v1/regions/boundaries');
  if (!res.ok) throw new Error(`Failed to fetch region boundaries: ${res.status}`);
  return (await res.json()).boundaries;
}

// ============================================
// COMPOSITION
// ============================================

export async function fetchRegionComponents(id: string): Promise<RegionComponent[]> {
  const res = await apiFetch(`/api/v1/regions/${id}/components`);
  if (!res.ok)
    throw new Error(await errorMessage(res, `Failed to fetch components: ${res.status}`));
  return (await res.json()).components;
}

/** Replace a region's composition. Throws ComponentOverlapError (409) with the
 *  conflicting geographies when one already belongs to another region. */
export async function replaceRegionComponents(
  id: string,
  components: Array<{ geoType: GeoType; geoRef: string; label: string; geojson?: unknown }>,
): Promise<RegionComponent[]> {
  const res = await apiFetch(`/api/v1/regions/${id}/components`, {
    method: 'PUT',
    body: JSON.stringify({ components }),
  });
  if (res.status === 409) {
    const data = await res.json().catch(() => ({}) as Record<string, unknown>);
    throw new ComponentOverlapError(
      typeof data.message === 'string' ? data.message : 'Geography conflict',
      Array.isArray(data.conflicts) ? (data.conflicts as ComponentConflict[]) : [],
    );
  }
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to save components: ${res.status}`));
  return (await res.json()).components;
}

/** Venues that would move into a region if the given geographies were added. */
export async function previewReassignment(
  id: string,
  add: Array<{ geoType: GeoType; geoRef: string; geojson?: unknown }>,
): Promise<PreviewVenue[]> {
  const res = await apiFetch(`/api/v1/regions/${id}/reassignment-preview`, {
    method: 'POST',
    body: JSON.stringify({ add }),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `Failed to preview reassignment: ${res.status}`));
  return (await res.json()).venues;
}

// ============================================
// VENUE ASSIGNMENT & EXCEPTIONS
// ============================================

/** Set a venue's region exception: pin to a region, exclude, or re-auto. */
export async function setVenueException(
  venueId: string,
  mode: AssignmentMode,
  regionId?: string | null,
): Promise<MapVenue> {
  const res = await apiFetch(`/api/v1/regions/venues/${venueId}/region`, {
    method: 'PUT',
    body: JSON.stringify({ mode, regionId: regionId ?? null }),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, `Failed to set venue exception: ${res.status}`));
  return (await res.json()).venue;
}

// ============================================
// DIRECTOR
// ============================================

export async function setRegionDirector(id: string, userId: string | null): Promise<RegionDetail> {
  const res = await apiFetch(`/api/v1/regions/${id}/director`, {
    method: 'PUT',
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `Failed to set director: ${res.status}`));
  return (await res.json()).region;
}

// ============================================
// GEOCODING (batch auto-assign)
// ============================================

export async function triggerGeocoding(): Promise<ReassignSummary> {
  const res = await apiFetch('/api/v1/regions/geocode-venues', { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to trigger geocoding: ${res.status}`);
  return (await res.json()).summary;
}

// ============================================
// GEO SEARCH (builder "add a place" → composition ref)
// ============================================

/** An area returned by the Nominatim level search, with its boundary polygon. */
export interface AreaSearchResult {
  geoType: GeoType;
  geoRef: string;
  label: string;
  geojson: unknown;
}

/**
 * Search areas of a level (state/county/city/neighborhood) for the builder's
 * type-and-typeahead picker. Each result carries its boundary polygon so the
 * picked area can be previewed on the map and stored on save.
 */
export async function searchAreasByLevel(
  query: string,
  level: GeoType,
): Promise<AreaSearchResult[]> {
  const res = await apiFetch(
    `/api/v1/regions/area-search?q=${encodeURIComponent(query)}&level=${level}`,
  );
  if (!res.ok) return [];
  return (await res.json()).results;
}

export interface ReverseAreaResult {
  /** The area containing the clicked point at the chosen level, or null. */
  result: AreaSearchResult | null;
  /** False when the area falls outside the region's parent bounds. */
  withinParent: boolean;
}

/** Reverse-geocode a clicked point to the containing area of `level`, for the
 *  builder's click-to-add map. `regionId` enables the parent-bounds check. */
export async function reverseAreaAt(
  lat: number,
  lon: number,
  level: GeoType,
  regionId: string,
): Promise<ReverseAreaResult> {
  const res = await apiFetch(
    `/api/v1/regions/area-at?lat=${lat}&lon=${lon}&level=${level}&regionId=${regionId}`,
  );
  if (!res.ok) throw new Error(`Failed to resolve area: ${res.status}`);
  return res.json();
}

// ============================================
// NOMINATIM (legacy stand-in)
// ============================================

export interface NominatimResult {
  name: string;
  displayName: string;
  type: string;
}

export async function reverseGeocodeArea(
  lat: number,
  lon: number,
  level: 'state' | 'county' | 'city',
): Promise<NominatimResult | null> {
  const res = await apiFetch(
    `/api/v1/regions/nominatim/reverse?lat=${lat}&lon=${lon}&zoom=${level}`,
  );
  if (!res.ok) return null;
  return (await res.json()).result;
}

export async function searchAreas(query: string): Promise<NominatimResult[]> {
  const res = await apiFetch(`/api/v1/regions/nominatim/search?q=${encodeURIComponent(query)}`);
  if (!res.ok) return [];
  return (await res.json()).results;
}
